<template>
  <base-slide-over ref="clarificationSlideover" title="Clarify Prescription Details" size="3xl" style="z-index: 10000">
    <base-content>
      <div v-if="isLoading" class="tw-flex tw-justify-center tw-py-5">
        <base-spinner />
      </div>
      <div v-else>
        <edit-business-member-prescription-medications
          :allowed-medications="allowedMedications"
          :max-medications="prescription.businessProgram ? prescription.businessProgram.maxMedications : 0"
          :edit-medications.sync="editMedications"
          :medications-label="dynamicLabel"
          hide-save
        />
        <div class="tw-pb-2">
          <edit-business-member-prescription-rx-items :edit-rx-items.sync="editRxItems" :allowed-rx-items="allowedRxItems" hide-save />
        </div>
      </div>

      <div class="tw-mt-5 tw-font-normal tw-text-sm">Comments:</div>
      <div v-if="isNotesLoading" class="tw-flex-col">
        <base-skeleton-loader class="tw-h-4 tw-w-1/2 tw-mt-2" />
        <base-skeleton-loader class="tw-h-4 tw-w-1/2 tw-mt-2" />
      </div>
      <div v-for="currentNote in notes" :key="currentNote.todoItemNoteId">
        <div class="tw-text-sm tw-text-gray-900 tw-mt-2">{{ currentNote.value }}</div>
        <div class="tw-text-xs tw-text-gray-500">{{ currentNote.insertByName }}: {{ customDateFormatter(currentNote.insertDate) }}</div>
      </div>
      <base-form ref="form" v-model="isFormValid" class="tw-mt-8">
        <base-textarea v-model="note" :label="`Clarifying Note: (visible to patient and RxHarmony staff)`" required />
      </base-form>
      <inline-error id="clarification-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-content>
    <template #fixed-bottom>
      <div class="tw-p-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-2" @click="hide()">
          Cancel
        </base-button>
        <base-button class="tw-w-auto" @click="submit" :loading="isSaving"> Submit </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import EditBusinessMemberPrescriptionMedications from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionMedications";
import EditBusinessMemberPrescriptionRxItems from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionRxItems";
import InlineError from "@/components/InlineError";
import { orderBy, isEqual } from "lodash";
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "Clarification",
  components: {
    EditBusinessMemberPrescriptionMedications,
    InlineError,
    EditBusinessMemberPrescriptionRxItems,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isLoading: false,
      isNotesLoading: false,
      isSaving: false,
      todoItemId: null,
      userId: null,
      businessPrescriptionId: null,
      prescription: {},
      editMedications: [],
      editRxItems: [],
      originalMedications: [],
      originalRxItems: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
      currentComponent: null,
      note: null,
      notes: [],
      isFormValid: true,
      rxItems: [],
    };
  },
  computed: {
    ...mapGetters("location", ["location", "locations"]),
    allowedMedications() {
      return this.prescription.businessProgram
        ? this.prescription.businessProgram.allowedMedications
            .map((am) => ({
              label: am.name.value,
              value: am.id,
              productGroup: am.productGroup,
            }))
            .filter((m) => !this.editMedications.find((em) => em.value === m.value))
        : [];
    },
    changesMade() {
      const newMedications = this.editMedications.map((m) => m.value).filter((m) => m);
      const medicationChanges = this.originalMedications.length !== newMedications.length || this.originalMedications.some((m, i) => m !== newMedications[i]);
      const noteChanges = this.note !== null && this.note !== "";
      // Check for changes in Rx Items
      const newRxItemValues = orderBy(this.editRxItems.map((item) => item.value));
      const originalRxItemValues = orderBy(this.originalRxItems.map((item) => item.value));
      const rxItemsChanged = !isEqual(newRxItemValues, originalRxItemValues);
      return medicationChanges || noteChanges || rxItemsChanged;
    },

    dynamicLabel() {
      const label = this.prescription && this.prescription.businessProgram && this.prescription.businessProgram.programFieldLabel;
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : "Medication";
    },
    allowedRxItems() {
      const currentProgram = this.prescription.businessProgram;

      if (!currentProgram || !this.rxItems || this.rxItems.length === 0) {
        return [];
      }

      const currentProgramId = currentProgram.id;
      const filtered = this.rxItems.filter((rxItem) => {
        const itemFilterType = rxItem.programFilterType;
        let itemProgramsIds = [];

        if (rxItem.programsId && typeof rxItem.programsId === "string") {
          try {
            itemProgramsIds = JSON.parse(rxItem.programsId);
          } catch (e) {
            itemProgramsIds = [];
          }
        } else if (Array.isArray(rxItem.programsId)) {
          itemProgramsIds = rxItem.programsId;
        }

        let isVisible = false;
        if (itemFilterType === null || itemFilterType === "all") {
          isVisible = true;
        } else if (itemFilterType === "include") {
          isVisible = itemProgramsIds.includes(currentProgramId);
        } else if (itemFilterType === "exclude") {
          isVisible = !itemProgramsIds.includes(currentProgramId);
        } else {
          isVisible = true;
        }
        // Exclude items already selected in editRxItems
        return isVisible && !this.editRxItems.find((eRi) => eRi.value === rxItem.businessItemCategoryId);
      });
      const mappedItems = filtered.map((item) => ({
        label: item.name,
        value: item.businessItemCategoryId,
        productGroup: item.productGroup,
      }));
      return mappedItems;
    },
  },
  methods: {
    show(todoItemId, userId, businessPrescriptionId) {
      this.todoItemId = todoItemId;
      this.userId = userId;
      this.businessPrescriptionId = businessPrescriptionId;
      this.getNotes();
      this.$refs.clarificationSlideover.show();
      this.currentComponent = "BusinessMemberPrescriptions";
      this.getPrescription();
      this.getRxItems();
    },
    hide() {
      this.reset();
      this.$refs.clarificationSlideover.hide();
    },
    reset() {
      this.todoItemId = null;
      this.userId = null;
      this.isFormValid = true;
      this.editMedications = [];
      this.originalMedications = [];
      this.editRxItems = [];
      this.originalRxItems = [];
    },

    async getPrescription() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/prescriptions/${this.businessPrescriptionId}`);
        this.prescription = response.data.prescription;
        this.editMedications = orderBy(this.prescription.medications, ["sortOrder"]).map((m) => ({
          label: m.businessItemCategory.name.value,
          value: m.businessItemCategory.id,
          productGroup: m.businessItemCategory.productGroup,
        }));
        this.originalMedications = this.editMedications.map((m) => m.value).filter((m) => m);

        this.editRxItems = orderBy(this.prescription.rxItems, ["sortOrder"]).map((item) => ({
          label: item.businessItemCategory && item.businessItemCategory.name ? item.businessItemCategory.name.value : "",
          value: item.businessItemCategory.id,
          productGroup: item.businessItemCategory.productGroup,
        }));
        this.originalRxItems = this.editRxItems.map((item) => ({ ...item }));
      } finally {
        this.isLoading = false;
      }
    },
    getNotes() {
      this.isNotesLoading = true;

      this.$axios
        .get(`/businesses/${this.businessId}/todo/${this.todoItemId}/notes`)
        .then((res) => {
          this.notes = res.data;
        })
        .finally(() => {
          this.isNotesLoading = false;
        });
    },
    submit() {
      if (!this.$refs.form.validate()) return;
      this.isSaving = true;

      this.$axios
        .post(`/businesses/${this.businessId}/todo/${this.todoItemId}/clarify?isWithChanges=${this.changesMade ? "true" : "false"}`, {
          businessItemCategoryIds: this.editMedications.map((m) => m.value).filter((m) => m),
          note: this.note,
          rxItemCategoryIds: this.editRxItems.map((rx) => rx.value).filter((id) => id),
        })
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Clarification Submitted",
            message: `Your clarification was submitted. Thank you.`,
          });
          this.EventBus.$emit("activitiesUpdated");
          this.$emit("save");
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error submitting clarification`;
          this.errorMessage = "Sorry, we had trouble submitting this clarification. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    customDateFormatter(date) {
      if (date == null) return "";

      return moment
        .utc(date)
        .local()
        .format("M/D/YYYY hh:mm a");
    },
    async getRxItems() {
      try {
        const url = `/businesses/${this.businessId}/item-categories/rx-items`;
        this.$axios.get(url).then((res) => {
          this.rxItems = res.data;
        });
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve Rx Items",
          message: "We were not able to retrieve your Rx Items. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
  },
};
</script>
